import { useEffect, useRef } from "react";

/**
 * useClipboard — Excel-style copy/paste for the grid (QA #11).
 *
 * Ctrl/Cmd+C copies the current selection (single cell or range) to the
 * system clipboard as tab-separated text — the same format Excel uses, so
 * it round-trips with desktop Excel and Google Sheets.
 *
 * Ctrl/Cmd+V (edit mode only) pastes clipboard text starting at the
 * selected cell; multi-cell TSV pastes as a block via ONE batched commit.
 *
 * Keystrokes inside real text fields (formula bar, in-cell editor) are
 * left to the browser's native clipboard handling.
 */
// Module-level internal clipboard: RPDs run under document-protection
// (copy guards) and the async Clipboard API needs a secure context +
// permission — either can silently break the system clipboard. Cell→cell
// copy/paste inside the viewer must always work, so the copied text is
// kept here and used as the paste fallback.
let internalClipboard = null;

export default function useClipboard({
  enabled, editMode, selectedCell, normalizedSelection, normalizedRows, commitCells,
}) {
  // Live state for the (stable) window listener.
  const stateRef = useRef({});
  stateRef.current = { editMode, selectedCell, normalizedSelection, normalizedRows, commitCells };

  useEffect(() => {
    if (!enabled) return undefined;

    const writeClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (_) { /* fall through to execCommand */ }
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) { /* clipboard unavailable */ }
    };

    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const key = String(e.key).toLowerCase();
      if (key !== "c" && key !== "v") return;
      const tag = String(e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return; // native behaviour
      const s = stateRef.current;

      if (key === "c") {
        const sel = s.normalizedSelection ||
          { r1: s.selectedCell.row, r2: s.selectedCell.row, c1: s.selectedCell.col, c2: s.selectedCell.col };
        const lines = [];
        for (let r = sel.r1; r <= sel.r2; r += 1) {
          const cells = [];
          for (let c = sel.c1; c <= sel.c2; c += 1) {
            const v = s.normalizedRows[r] ? s.normalizedRows[r][c] : "";
            cells.push(v == null ? "" : String(v));
          }
          lines.push(cells.join("\t"));
        }
        e.preventDefault();
        const text = lines.join("\n");
        internalClipboard = text;        // always works, even when the
        writeClipboard(text);            // system clipboard is blocked
        return;
      }

      // Paste — only while editing is on.
      if (!s.editMode || typeof s.commitCells !== "function") return;
      e.preventDefault();
      const applyPaste = (text) => {
        if (text == null || text === "") return;
        const st = stateRef.current;
        const rows = String(text).replace(/\r\n?/g, "\n").split("\n");
        if (rows.length > 1 && rows[rows.length - 1] === "") rows.pop();
        const edits = [];
        rows.forEach((line, i) => {
          line.split("\t").forEach((val, j) => {
            edits.push({ row: st.selectedCell.row + i, col: st.selectedCell.col + j, text: val });
          });
        });
        if (edits.length) st.commitCells(edits);
      };
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText()
          .then((text) => applyPaste(text || internalClipboard))
          .catch(() => applyPaste(internalClipboard));
      } else {
        applyPaste(internalClipboard);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
